import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import {
  ClearModal, DuplicateModal, NoteModal, ObjectivesModal, SlotModal,
} from '../components/AdminPanels';
import { api } from '../utils/api';
import {
  JOURS_COURTS, MOIS, addWeeks, formatDate, formatDateISO, generateTimeSlots,
  getISOWeekNumber, getMonday, getWeekDays,
} from '../utils/dates';
import {
  decorateDayEvents, derivePauses, mergeScheduleSlots, timeToMinutes,
} from '../utils/planningTest';

const ROW_HEIGHT = 34;
const MOIS_COURTS = ['JANV.', 'FÉVR.', 'MARS', 'AVR.', 'MAI', 'JUIN', 'JUIL.', 'AOÛT', 'SEPT.', 'OCT.', 'NOV.', 'DÉC.'];

function StatusIcon({ type, className = '' }) {
  if (type === 'pause') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M5 8h11v5a5 5 0 0 1-5 5H10a5 5 0 0 1-5-5V8Z" />
        <path d="M16 10h2a3 3 0 0 1 0 6h-2" />
        <path d="M8 5c1-1 .2-2 1-3M12 5c1-1 .2-2 1-3M4 21h16" />
      </svg>
    );
  }
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <rect x="3" y="7" width="18" height="14" rx="2" />
      <path d="M3 12h18M8 11v3M16 11v3" />
    </svg>
  );
}

function formatWeekRange(start, end) {
  const startDay = String(start.getDate()).padStart(2, '0');
  const endDay = String(end.getDate()).padStart(2, '0');
  const startMonth = MOIS_COURTS[start.getMonth()];
  const endMonth = MOIS_COURTS[end.getMonth()];
  const year = end.getFullYear();

  return start.getMonth() === end.getMonth()
    ? `${startDay} – ${endDay} ${endMonth} ${year}`
    : `${startDay} ${startMonth} – ${endDay} ${endMonth} ${year}`;
}

function ObjectiveCard({ period }) {
  const visibleLines = (period.lines || []).filter(line => String(line.value_text || '').trim());
  return (
    <section className="planning-test-objective">
      <div className="planning-test-objective-title">
        <span>{MOIS[period.month - 1]} {period.year}</span><span aria-hidden="true">✎</span>
      </div>
      {visibleLines.length ? visibleLines.map(line => (
        <div className="planning-test-objective-row" key={line.line_order}>
          <span>{line.objective_text || `Objectif ${line.line_order}`}</span>
          <strong>{line.value_text}{line.line_order <= 2 ? ' €' : ''}</strong>
        </div>
      )) : <div className="planning-test-objective-empty">Non défini</div>}
    </section>
  );
}

function HoursCard({ icon, title, hours }) {
  return (
    <section className="planning-test-hours-card">
      <h3><span>{icon}</span>{title}</h3>
      <div className="planning-test-hours-list">
        {hours.length ? hours.map(item => (
          <div className="planning-test-hour" key={item.user_id}>
            <span className="planning-test-dot" style={{ background: item.color }} />
            <span>{item.username}</span><strong>{Number(item.total_hours).toFixed(1)}h</strong>
          </div>
        )) : <span className="planning-test-empty">Aucune heure</span>}
      </div>
    </section>
  );
}

function TimelineEvent({ event, openingMinutes }) {
  const start = timeToMinutes(event.start_time);
  const end = timeToMinutes(event.end_time);
  const duration = end - start;
  const style = {
    top: ((start - openingMinutes) / 30) * ROW_HEIGHT + 4,
    height: Math.max((duration / 30) * ROW_HEIGHT - 8, 24),
    '--event-color': event.user_color || '#64748b',
    left: event.hasOverlap ? `calc(${event.lane * 50}% + 4px)` : '4px',
    width: event.hasOverlap ? 'calc(50% - 7px)' : 'calc(100% - 8px)',
  };
  const compact = duration <= 60;
  const initial = String(event.username || '?').slice(0, 1).toUpperCase();
  const isPause = event.status === 'pause';
  const isLeave = event.status === 'conge';

  return (
    <div className={`planning-test-event status-${event.status} ${compact ? 'is-compact' : ''}`} style={style}
      title={`${event.username} · ${event.start_time}–${event.end_time}`}>
      {isPause ? (
        <div className="planning-test-pause-label">
          <StatusIcon type="pause" className="planning-test-coffee" />
          <span className="planning-test-pause-text"><strong>Pause</strong><b>{event.start_time} – {event.end_time}</b></span>
        </div>
      ) : (
        <>
          <div className="planning-test-event-heading">{isLeave
            ? <StatusIcon type="leave" className="planning-test-leave-icon" />
            : <span className="planning-test-initial">{initial}</span>}<span>{event.start_time} – {event.end_time}</span></div>
          {event.status !== 'present' && <div className="planning-test-event-status">{event.status === 'conge' ? 'Congé' : 'Absence'}</div>}
          {isLeave && <StatusIcon type="leave" className="planning-test-leave-watermark" />}
        </>
      )}
    </div>
  );
}

export default function PlanningTestPage({ onStoreResolved }) {
  const { user, canManageStore } = useAuth();
  const toast = useToast();
  const [currentMonday, setCurrentMonday] = useState(getMonday(new Date()));
  const [selectedStoreId, setSelectedStoreId] = useState(() => {
    const saved = Number(localStorage.getItem('planningTestStoreId'));
    return Number.isInteger(saved) && saved > 0 ? saved : null;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [managementOpen, setManagementOpen] = useState(false);
  const [showObjectives, setShowObjectives] = useState(false);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showDuplicate, setShowDuplicate] = useState(null);
  const [showClear, setShowClear] = useState(null);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [holidayDate, setHolidayDate] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [objMonth, setObjMonth] = useState(new Date().getMonth() + 1);
  const [objYear, setObjYear] = useState(new Date().getFullYear());
  const managementRef = useRef(null);
  const weekDays = useMemo(() => getWeekDays(currentMonday), [currentMonday]);
  const startISO = formatDateISO(currentMonday);
  const endISO = formatDateISO(weekDays[6]);

  const loadData = useCallback(async () => {
    let retryWithoutSavedStore = false;
    setLoading(true); setError('');
    try {
      const result = await api.getPlanningTest(selectedStoreId, startISO, endISO);
      setData(result);
      setSelectedStoreId(current => current || result.store.id);
      localStorage.setItem('planningTestStoreId', String(result.store.id));
      localStorage.setItem('currentStoreId', String(result.store.id));
      onStoreResolved?.(result.store);
    } catch (requestError) {
      if (requestError.status === 403 && selectedStoreId) {
        retryWithoutSavedStore = true;
        localStorage.removeItem('planningTestStoreId');
        setSelectedStoreId(null);
      } else {
        setError(requestError.message); toast(requestError.message, 'error');
      }
    } finally {
      if (!retryWithoutSavedStore) setLoading(false);
    }
  }, [selectedStoreId, startISO, endISO, onStoreResolved, toast]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    const closeManagement = event => {
      if (managementRef.current && !managementRef.current.contains(event.target)) setManagementOpen(false);
    };
    document.addEventListener('mousedown', closeManagement);
    return () => document.removeEventListener('mousedown', closeManagement);
  }, []);
  if (String(user?.username || '').trim().toLowerCase() !== 'philippe') return null;

  const store = data?.store;
  const availableStores = data?.stores || [];
  const canManagePlanning = Boolean(store && canManageStore('manage_planning', store.id));
  const canManageObjectives = Boolean(store && canManageStore('manage_objectives', store.id));
  const timeSlots = generateTimeSlots(store?.opening_time || '09:00', store?.closing_time || '19:00');
  const openingMinutes = timeToMinutes(store?.opening_time || '09:00');
  const calendarHeight = timeSlots.length * ROW_HEIGHT;
  const mergedSlots = mergeScheduleSlots(data?.slots || []);
  const scheduledUserIds = new Set(mergedSlots.map(slot => Number(slot.user_id)));
  const allUsers = data?.users || [];
  const scheduledUsers = allUsers.filter(member => scheduledUserIds.has(Number(member.id)));
  const laneUsers = scheduledUsers.length ? scheduledUsers : allUsers;
  const todayISO = formatDateISO(new Date());
  const eventsFor = (date, userId) => {
    const normalizedSlots = mergedSlots
      .filter(slot => slot.date === date && Number(slot.user_id) === Number(userId))
      .map(slot => (slot.status === 'absent' ? { ...slot, status: 'pause' } : slot));
    const events = decorateDayEvents(normalizedSlots);
    const inferredPauses = derivePauses(events).filter(pause => !events.some(event => (
      event.status === 'pause'
      && event.start_time === pause.start_time
      && event.end_time === pause.end_time
    )));
    return [...events, ...inferredPauses];
  };
  const handleAddHoliday = async () => {
    if (!holidayDate) return toast('Sélectionnez une date', 'error');
    if (new Date(`${holidayDate}T12:00:00`).getDay() === 0) return toast('Le dimanche est déjà non ouvré', 'error');
    try {
      await api.createHoliday({ store_id: store.id, date: holidayDate });
      toast('Jour férié ajouté', 'success');
      setShowHolidayModal(false);
      setHolidayDate('');
      loadData();
    } catch (requestError) { toast(requestError.message, 'error'); }
  };
  const handleStoreChange = event => {
    const storeId = Number(event.target.value);
    if (!availableStores.some(item => item.id === storeId) || storeId === store?.id) return;
    localStorage.setItem('planningTestStoreId', String(storeId));
    localStorage.setItem('currentStoreId', String(storeId));
    setSelectedStoreId(storeId);
  };

  return (
    <main className="main-content planning-test-page">
      <style media="print">{'@page { size: A4 landscape; margin: 6mm; }'}</style>
      {loading ? <div className="planning-test-state loading-pulse">Chargement du planning de test…</div> : error ? (
        <div className="planning-test-state planning-test-error"><strong>Impossible d’afficher le prototype</strong><span>{error}</span><button className="btn btn-secondary" onClick={loadData}>Réessayer</button></div>
      ) : (
        <>
        <div className="planning-test-layout">
          <aside className="planning-test-sidebar">
            <label className={`planning-test-store-selector ${availableStores.length <= 1 ? 'is-single' : ''}`}>
              <span>Magasin actif</span>
              <span className="planning-test-store-field">
                <i style={{ background: store?.color }} aria-hidden="true" />
                {availableStores.length > 1 ? (
                  <select value={store?.id || ''} onChange={handleStoreChange} aria-label="Magasin actif">
                    {availableStores.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                ) : <strong>{store?.name}</strong>}
              </span>
            </label>
            <button className="planning-test-today" onClick={() => setCurrentMonday(getMonday(new Date()))}><span>▣</span>Aujourd’hui</button>
            <div className="planning-test-week-nav">
              <button onClick={() => setCurrentMonday(addWeeks(currentMonday, -1))} aria-label="Semaine précédente">‹</button>
              <div><strong>Semaine {getISOWeekNumber(currentMonday)}</strong><span>{formatWeekRange(currentMonday, weekDays[6])}</span></div>
              <button onClick={() => setCurrentMonday(addWeeks(currentMonday, 1))} aria-label="Semaine suivante">›</button>
            </div>

            <h2>Équipe</h2>
            <div className="planning-test-legend">{allUsers.map(member => <div key={member.id}><span style={{ background: member.color }} />{member.username}</div>)}</div>
            <div className="planning-test-divider" />
            <h2>Objectifs mensuels</h2>
            {(data.objectives || []).map(period => <ObjectiveCard key={`${period.year}-${period.month}`} period={period} />)}
            <div className="planning-test-sidebar-bottom">
              <div className="planning-test-divider" />
              <div className="planning-test-sidebar-hours">
                <HoursCard icon="📊" title={`Heures semaine (S${getISOWeekNumber(currentMonday)})`} hours={data.weekly_hours || []} />
                <HoursCard icon="▣" title={`Heures mois (${MOIS[(data.month || 1) - 1]})`} hours={data.monthly_hours || []} />
              </div>
            </div>
          </aside>

          <section className="planning-test-workspace">
            <div className="planning-test-calendar-stack">
            <div className="planning-test-toolbar">
              <span>Prototype privé · {store?.name}</span>
              {canManagePlanning && (
                <div className="admin-menu planning-test-management" ref={managementRef}>
                  <button className="planning-test-manage-button" onClick={() => setManagementOpen(open => !open)}>☰ Gestion des horaires</button>
                  {managementOpen && (
                    <div className="admin-menu-dropdown planning-test-management-menu">
                      <button className="admin-menu-item" onClick={() => { setSelectedDate(startISO); setShowSlotModal(true); setManagementOpen(false); }}>➕ Ajouter un créneau</button>
                      <div className="admin-menu-divider" />
                      <button className="admin-menu-item" onClick={() => { setShowDuplicate('day'); setManagementOpen(false); }}>📋 Dupliquer une journée</button>
                      <button className="admin-menu-item" onClick={() => { setShowDuplicate('week'); setManagementOpen(false); }}>📋 Dupliquer la semaine</button>
                      <div className="admin-menu-divider" />
                      <button className="admin-menu-item" onClick={() => { setShowClear('day'); setManagementOpen(false); }}>🗑️ Effacer une journée</button>
                      <button className="admin-menu-item" onClick={() => { setShowClear('week'); setManagementOpen(false); }}>🗑️ Effacer la semaine</button>
                      <div className="admin-menu-divider" />
                      <button className="admin-menu-item" onClick={() => { setHolidayDate(''); setShowHolidayModal(true); setManagementOpen(false); }}>🎌 Jour férié</button>
                      <button className="admin-menu-item" onClick={() => { setSelectedDate(todayISO); setShowNoteModal(true); setManagementOpen(false); }}>📝 Ajouter une note</button>
                      {canManageObjectives && <button className="admin-menu-item" onClick={() => { setObjMonth(new Date().getMonth() + 1); setObjYear(new Date().getFullYear()); setShowObjectives(true); setManagementOpen(false); }}>🎯 Objectifs mensuels</button>}
                    </div>
                  )}
                </div>
              )}
              <button className="planning-test-pdf" onClick={() => window.print()}><span>▧</span> PDF</button>
            </div>
            <section className="planning-test-calendar-shell">
              <div className="planning-test-calendar" style={{ '--timeline-height': `${calendarHeight}px`, '--lane-count': Math.max(laneUsers.length, 1) }}>
                <div className="planning-test-axis">
                  <div className="planning-test-axis-header"><span className="planning-test-clock" aria-hidden="true" /></div>
                  <div className="planning-test-axis-body" style={{ height: calendarHeight }}>{timeSlots.map((slot, index) => <span key={slot.start} style={{ top: index * ROW_HEIGHT + ROW_HEIGHT / 2 }}>{slot.start}</span>)}</div>
                </div>
                {weekDays.map((day, index) => {
                  const date = formatDateISO(day);
                  const notes = (data.notes || []).filter(note => note.date === date);
                  const noteText = notes.map(note => String(note.content || '').trim()).filter(Boolean).join(' • ');
                  const holiday = (data.holidays || []).some(item => item.date === date);
                  return (
                    <div className={`planning-test-day ${date === todayISO ? 'is-today' : ''} ${holiday ? 'is-holiday' : ''}`} key={date}>
                      <div className="planning-test-day-title" title={noteText || undefined}>
                        <span className="planning-test-day-label"><strong>{JOURS_COURTS[index]}</strong><span>{formatDate(day)}</span>{date === todayISO && <em>Aujourd’hui</em>}</span>
                        {noteText && <span className="planning-test-note"><i aria-hidden="true">✎</i><span>{noteText}</span></span>}
                      </div>
                      <div className="planning-test-user-headings">{laneUsers.map(member => <span key={member.id} style={{ color: member.color }}>{member.username}</span>)}</div>
                      <div className="planning-test-day-lanes" style={{ height: calendarHeight }}>
                        {laneUsers.map(member => {
                          const events = eventsFor(date, member.id);
                          return <div className="planning-test-user-lane" key={member.id}>{events.map(event => <TimelineEvent key={event.id || event.source_ids?.join('-')} event={event} openingMinutes={openingMinutes} />)}</div>;
                        })}
                        {holiday && <div className="planning-test-holiday-label">Férié</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
            </div>
          </section>
        </div>
        {canManageObjectives && <ObjectivesModal isOpen={showObjectives} onClose={() => setShowObjectives(false)} storeId={store.id} month={objMonth} year={objYear} onRefresh={loadData} />}
        {canManagePlanning && (
          <>
            <SlotModal isOpen={showSlotModal} onClose={() => setShowSlotModal(false)} storeId={store.id} storeUsers={allUsers} defaultDate={selectedDate} defaultTime={store.opening_time || '09:00'} editSlot={null} onRefresh={loadData} />
            <NoteModal isOpen={showNoteModal} onClose={() => setShowNoteModal(false)} storeId={store.id} date={selectedDate} editNote={null} onRefresh={loadData} />
            <DuplicateModal isOpen={!!showDuplicate} onClose={() => setShowDuplicate(null)} type={showDuplicate} storeId={store.id} weekStart={startISO} weekEnd={endISO} onRefresh={loadData} />
            <ClearModal isOpen={!!showClear} onClose={() => setShowClear(null)} type={showClear} storeId={store.id} storeUsers={allUsers} date={startISO} weekStart={startISO} weekEnd={endISO} onRefresh={loadData} />
          </>
        )}
        {showHolidayModal && (
          <div className="modal-overlay" onClick={() => setShowHolidayModal(false)}>
            <div className="modal-content" onClick={event => event.stopPropagation()} style={{ maxWidth: 400 }}>
              <div className="modal-header"><h2>🎌 Ajouter un jour férié</h2><button className="modal-close" onClick={() => setShowHolidayModal(false)}>&times;</button></div>
              <div style={{ padding: 20 }}>
                <p style={{ fontSize: '.85rem', color: 'var(--text-muted)', marginBottom: 16 }}>Le jour sélectionné sera marqué comme férié pour <strong>{store.name}</strong>.</p>
                <div className="form-group"><label>Date</label><input className="form-input" type="date" value={holidayDate} onChange={event => setHolidayDate(event.target.value)} /></div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
                  <button className="btn btn-secondary" onClick={() => setShowHolidayModal(false)}>Annuler</button>
                  <button className="btn btn-primary" onClick={handleAddHoliday} disabled={!holidayDate}>Ajouter</button>
                </div>
              </div>
            </div>
          </div>
        )}
        </>
      )}
    </main>
  );
}
